// Referrer flags from gate-status (isReferrerOnly / canActAsReferrer), stored next
// to the live flag (supplierLive.ts) and read by the menu. Both default to false,
// so a missing value never shows referral items to an ordinary supplier.
import { bumpNavFlags } from "./navFlagStore";

const KEY = "supplier_referrer_flags";

// referrerFullOnboarding: legacy (an affiliate from before 2026-10-19 that took the
// full onboarding); kept so an older cached value still parses.
// serviceProviderUpgrade: an affiliate's optional "become a service provider"
// upgrade (NONE / IN_PROGRESS / SUBMITTED / APPROVED / REJECTED). While it runs,
// the affiliate also sees the onboarding steps.
export type ReferrerFlags = {
  isReferrerOnly: boolean;
  canActAsReferrer: boolean;
  referrerFullOnboarding: boolean;
  serviceProviderUpgrade: string;
};

export const getReferrerFlags = (): ReferrerFlags => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "{}");
    return {
      isReferrerOnly: !!raw.isReferrerOnly,
      canActAsReferrer: !!raw.canActAsReferrer,
      referrerFullOnboarding: !!raw.referrerFullOnboarding,
      serviceProviderUpgrade: typeof raw.serviceProviderUpgrade === "string" ? raw.serviceProviderUpgrade : "NONE",
    };
  } catch {
    return { isReferrerOnly: false, canActAsReferrer: false, referrerFullOnboarding: false, serviceProviderUpgrade: "NONE" };
  }
};

export const setReferrerFlags = (f: Partial<ReferrerFlags>): void => {
  try {
    const next = { ...getReferrerFlags(), ...f };
    localStorage.setItem(
      KEY,
      JSON.stringify({
        isReferrerOnly: !!next.isReferrerOnly,
        // A referrer-only company can always refer (the backend enforces the same).
        canActAsReferrer: !!next.canActAsReferrer || !!next.isReferrerOnly,
        referrerFullOnboarding: !!next.referrerFullOnboarding,
        serviceProviderUpgrade: next.serviceProviderUpgrade || "NONE",
      })
    );
  } catch {
    /* storage unavailable */
  }
  bumpNavFlags();
};

/** What a referrer-only company sees. Onboarding is added while it is not live. */
export const REFERRER_MENU_KEYS = [
  "dashboard",
  "referral-links",
  "referees",
  "referral-earnings",
  "finance",
  "bank-details",
  "support",
  "ticket",
  "team",
  "settings",
];

/**
 * What an affiliate sees before Verati takes it live: help and onboarding only —
 * no dashboard, referrals or finance until it can actually refer (owner, 2026-10-19).
 */
export const REFERRER_ONBOARDING_MENU_KEYS = ["support", "ticket", "team", "settings"];

/** An affiliate's service-provider upgrade is under way: it answers the onboarding steps too. */
export const upgradeInProgress = (u?: string | null): boolean => u === "IN_PROGRESS" || u === "SUBMITTED" || u === "REJECTED";
